import React from 'react';

import { ContactsOutlined } from '@mui/icons-material';
import { useCurrentuser } from '@uplift/contexts';
import { useIsCurrentProviderLive, useL10n } from '@uplift/hooks';
import { FlexTable, FlexTableColumn, NoItemsLoadingBody, TableCellDateTime } from '@uplift/ui-v2';

import { ActionCell } from './ActionCell.component';
import { PatientRow } from './PatientsTable.types';
import { UserNameCell } from './UserNameCell.component';

const sessionComparator = (_v1: unknown, _v2: unknown, row1: PatientRow, row2: PatientRow, dir: 'asc' | 'desc') => {
  const firstSession = row1.nextSession;
  const secondSession = row2.nextSession;

  if (firstSession && !secondSession) {
    return dir === 'asc' ? -1 : 1;
  } else if ((!firstSession && secondSession) || (!firstSession && !secondSession)) {
    return dir === 'asc' ? 1 : -1;
  }

  if (firstSession.isBefore(secondSession)) {
    return -1;
  } else if (firstSession.isAfter(secondSession)) {
    return 1;
  } else {
    return 0;
  }
};

export type PatientsTableProps = {
  rows: PatientRow[];
  loading: boolean;
  isMobile?: boolean;
  onRowClick?: (row: PatientRow) => void;
};

export const PatientsTable: React.FC<PatientsTableProps> = (props) => {
  const l10n = useL10n();
  const isDesktop = !props.isMobile;
  const me = useCurrentuser();
  const isProviderInLiveStatus = useIsCurrentProviderLive(me);

  const patientsTableColumns = React.useMemo(() => {
    if (props.isMobile) {
      return [
        {
          field: 'name',
          headerName: l10n.patients.name,
          renderCell: UserNameCell,
          flex: 1,
          sortable: true,
        } as FlexTableColumn<PatientRow, 'name'>,
        {
          field: 'nextSession',
          headerName: l10n.patients.nextSession,
          renderCell: (props) => props.value ? <TableCellDateTime value={props.value} /> : 'Not scheduled',
          width: 120,
          sortComparator: sessionComparator,
        } as FlexTableColumn<PatientRow, 'nextSession'>,
        {
          field: 'id',
          headerName: ' ',
          renderCell: ActionCell,
          width: 70,
        } as FlexTableColumn<PatientRow, 'id'>,
      ];
    }

    return [
      {
        field: 'name',
        headerName: l10n.patients.name,
        renderCell: UserNameCell,
        sortable: true
      } as FlexTableColumn<PatientRow, 'name'>,
      {
        field: 'nextSession',
        headerName: l10n.patients.nextSession,
        renderCell: (props) => props.value ? <TableCellDateTime value={props.value} /> : 'Not scheduled',
        sortable: true,
        sortComparator: sessionComparator
      } as FlexTableColumn<PatientRow, 'nextSession'>,
      {
        field: 'lastSession',
        headerName: l10n.patients.lastSession,
        renderCell: (props) => props.value ? <TableCellDateTime value={props.value} /> : '-'
      } as FlexTableColumn<PatientRow, 'lastSession'>,
      {
        field: 'location',
        headerName: l10n.patients.location,
      } as FlexTableColumn<PatientRow, 'location'>,
      {
        field: 'status',
        headerName: l10n.patients.status,
        renderCell: props => <span style={{ textTransform: 'capitalize' }}>{props.value}</span>,
      } as FlexTableColumn<PatientRow, 'status'>,
      {
        field: 'id',
        headerName: ' ',
        renderCell: ActionCell,
        align: 'right',
      } as FlexTableColumn<PatientRow, 'id'>,
    ];
  }, [ l10n, props.isMobile ]);

  if (props.loading) {
    return <NoItemsLoadingBody />;
  }

  const emptyOverlayContent = isProviderInLiveStatus ? {
    icon: <ContactsOutlined fontSize="inherit" />,
    message: 'No Clients'
  } : {
    icon: <ContactsOutlined fontSize="inherit" color="action"/>,
    message: 'Client Directory',
    subtitle: 'All the clients that you’ve seen will be shown here.',
  };

  return (
    <FlexTable
      onRowClick={props.onRowClick}
      columns={patientsTableColumns}
      rows={props.rows}
      pinColumn={props.isMobile ? undefined : [ 'start', 'end' ]}
      infiniteScroll={{
        perPage: 20
      }}
      horizontalScroll={isDesktop}
      emptyOverlayProps={emptyOverlayContent}
      initialSort={{
        colId: 'name',
        type: 'asc'
      }}
    />
  );
};
